import React from 'react';
import FhirService from '@lifeomic/phc-web-toolkit/src/services/FhirService';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import {
  useActiveAccountId,
  useActiveProjectId,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import useToggle from '@lifeomic/phc-web-toolkit/src/hooks/useToggle';

const fhirService = new FhirService();

type useLoadReferenceResourcesReturnValue = {
  newResource: fhir.Resource | null;
  prevResource?: fhir.Resource | null;
  isLoading: boolean;
  error: boolean;
};

export const useLoadReferencedResources = (
  event: fhir.AuditEvent | null,
): useLoadReferenceResourcesReturnValue => {
  const [getResource] = useService(fhirService.openGet);
  const activeAccountId = useActiveAccountId();
  const activeProjectId = useActiveProjectId();

  const [isLoading, setIsLoading, setLoadingFinished] = useToggle(true);
  const [newResource, setNewResource] = React.useState<fhir.Resource | null>(
    null,
  );
  const [prevResource, setPrevResource] = React.useState<fhir.Resource | null>(
    null,
  );
  const [error, setError] = React.useState<boolean>(false);

  const fetchReferencedResources = React.useCallback(async () => {
    setIsLoading();

    try {
      const newResourceUrl = event?.entity?.[1]?.reference?.reference;
      const prevResourceUrl = event?.entity?.[0]?.reference?.reference;

      const requests = [];
      if (newResourceUrl)
        requests.push(
          getResource(activeAccountId, newResourceUrl, {
            _tag: `http://lifeomic.com/fhir/dataset|${activeProjectId}`,
          }),
        );
      if (prevResourceUrl)
        requests.push(
          getResource(activeAccountId, prevResourceUrl, {
            _tag: `http://lifeomic.com/fhir/dataset|${activeProjectId}`,
          }),
        );

      const referencedResources = await Promise.all(requests);
      setNewResource(referencedResources[0]?.data);
      setPrevResource(referencedResources[1]?.data);
    } catch (e) {
      console.error(e);
      setError(true);
    } finally {
      setLoadingFinished();
    }
  }, [
    event,
    activeAccountId,
    activeProjectId,
    getResource,
    setIsLoading,
    setLoadingFinished,
  ]);

  const resetResources = () => {
    setNewResource(null);
    setPrevResource(null);
  };

  React.useEffect(() => {
    if (event) fetchReferencedResources();
    else resetResources();
  }, [event, fetchReferencedResources]);

  return {
    newResource,
    prevResource,
    isLoading,
    error,
  };
};
